import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, EyeIcon, EyeOffIcon, LockIcon, AlertCircleIcon } from 'lucide-react';
import { Shell, Section, Eyebrow, GoldRule } from '../../components/ui/Layout';
import { Button, ButtonLink } from '../../components/ui/Button';
import { applyPageMeta } from '../../utils/seo';
import { useRegistry } from '../../contexts/RegistryContext';
import { publishRegistration } from '../../utils/sharedInbox';
type FieldKey = 'fullName' | 'email' | 'professionalTitle' | 'yearsExperience' | 'linkedInUrl' | 'password' | 'confirmPassword' | 'motivation';
type FormState = Record<FieldKey, string>;
type Errors = Partial<Record<FieldKey, string>>;
const emptyForm: FormState = {
  fullName: '',
  email: '',
  professionalTitle: '',
  yearsExperience: '',
  linkedInUrl: '',
  password: '',
  confirmPassword: '',
  motivation: ''
};
const whatHappensNext = ['A consultant reviews every submission personally. There is no automated screening.', 'Registering places you in our research network. It is not an application, and it does not guarantee consideration for any particular mandate.', 'Confidential opportunities are shared to your portal, not broadcast by email.', 'Nothing about you reaches an employer without your written authorization for that specific mandate.'];
function validate(form: FormState): Errors {
  const errors: Errors = {};
  if (!form.fullName.trim()) {
    errors.fullName = 'Enter your full name.';
  }
  if (!form.email.trim()) {
    errors.email = 'Enter your email address.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) {
    errors.email = 'Enter a valid email address, for example name@company.com.';
  }
  if (form.yearsExperience.trim()) {
    const years = Number(form.yearsExperience);
    if (!Number.isFinite(years) || years < 0 || years > 60) {
      errors.yearsExperience = 'Enter a number of years between 0 and 60.';
    }
  }
  if (form.linkedInUrl.trim() && !/linkedin\.com\//i.test(form.linkedInUrl.trim())) {
    errors.linkedInUrl = 'Enter a full LinkedIn profile address, for example linkedin.com/in/yourname.';
  }
  if (!form.password) {
    errors.password = 'Create a password for your candidate portal.';
  } else if (form.password.length < 10) {
    errors.password = 'Use at least 10 characters.';
  }
  if (!form.confirmPassword) {
    errors.confirmPassword = 'Re-enter your password.';
  } else if (form.confirmPassword !== form.password) {
    errors.confirmPassword = 'The two passwords do not match.';
  }
  return errors;
}
const labelClass = 'block font-sans text-[0.8125rem] font-medium text-ink';
const hintClass = 'mt-1 font-sans text-[0.75rem] leading-relaxed text-charcoal-300';
function controlClass(hasError: boolean): string {
  return ['mt-2 w-full rounded-sm border bg-white px-3.5 py-2.5 font-sans text-[0.9375rem] text-ink', 'placeholder:text-charcoal-200 transition-colors duration-120 ease-institutional', hasError ? 'border-state-negative' : 'border-navy-100 hover:border-navy-300 focus:border-navy-500'].join(' ');
}
function FieldError({
  id,
  message
}: {
  id: string;
  message?: string;
}) {
  if (!message) return null;
  return <p id={id} className="mt-1.5 flex items-start gap-1.5 font-sans text-[0.75rem] leading-relaxed text-state-negative" data-id="element-902" data-mp="10lf31rplnmef">
      <AlertCircleIcon aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-903" data-mp="1ejv535ciooq8" />
      {message}
    </p>;
}
export function Register() {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Errors>({});
  const [submitted, setSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [reference, setReference] = useState('');
  const {
    addRegistration
  } = useRegistry();
  const errorSummaryRef = useRef<HTMLDivElement>(null);
  const confirmationRef = useRef<HTMLDivElement>(null);
  const uid = useId();
  const fieldId = (key: FieldKey) => `${uid}-${key}`;
  const errorId = (key: FieldKey) => `${uid}-${key}-error`;
  useEffect(() => {
    applyPageMeta({
      title: 'Register your interest | Velnoris Executive Network',
      description: 'Register with Velnoris Executive Network. Tell us about your background and ambitions, and create your candidate portal account. Every submission is reviewed by a consultant.',
      canonicalPath: '/register'
    });
  }, []);
  const errorList = useMemo(() => (Object.entries(errors) as [FieldKey, string][]).filter(([, message]) => Boolean(message)), [errors]);
  const update = (key: FieldKey) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      value
    } = event.target;
    setForm(prev => ({
      ...prev,
      [key]: value
    }));
    setErrors(prev => prev[key] ? {
      ...prev,
      [key]: undefined
    } : prev);
  };
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length) {
      window.requestAnimationFrame(() => errorSummaryRef.current?.focus());
      return;
    }
    const record = addRegistration({
      fullName: form.fullName.trim(),
      email: form.email.trim(),
      professionalTitle: form.professionalTitle.trim(),
      yearsExperience: form.yearsExperience.trim(),
      linkedInUrl: form.linkedInUrl.trim(),
      motivation: form.motivation.trim()
    });
    setReference(record.reference);
    setSubmitted(true);

    /* Publish so the owner console can see it from any device. */
    void publishRegistration({
      fullName: record.fullName,
      email: record.email,
      professionalTitle: form.professionalTitle.trim(),
      yearsExperience: form.yearsExperience.trim(),
      linkedInUrl: form.linkedInUrl.trim(),
      motivation: form.motivation.trim(),
      reference: record.reference,
      submittedAt: record.submittedAt
    });
    window.requestAnimationFrame(() => confirmationRef.current?.focus());
  };
  if (submitted) {
    return <main id="main" data-id="element-904" data-mp="6zrhltug1axa">
        <Section tone="parchment" data-id="element-905" data-mp="712ldtnw2ioe">
          <div ref={confirmationRef} tabIndex={-1} className="max-w-2xl animate-rise-in border-t-2 border-ink bg-white p-8 outline-none md:p-12" data-id="element-906" data-mp="3pd7dsxdwehzd">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gold text-ink" data-id="element-907" data-mp="lwi8feq1xeba">
              <CheckIcon aria-hidden="true" className="h-5 w-5" data-id="element-908" data-mp="pyc5ld11g06p" />
            </span>
            <h1 className="mt-6 font-serif text-[1.75rem] leading-[1.15] text-ink md:text-[2.25rem]" data-id="element-909" data-mp="byb5hzslkwsa">
              Registration received
            </h1>
            <p className="mt-4 font-serif text-[1.0625rem] leading-relaxed text-charcoal-700" data-id="element-910" data-mp="gvkrt6kowsbw">
              Thank you, {form.fullName.split(' ')[0]}. Your registration reference is{' '}
              <span className="font-sans text-[0.9375rem] text-ink" data-id="element-911" data-mp="31o62n2doksqe">{reference}</span>. We have sent a confirmation to{' '}
              {form.email}.
            </p>
            <ul className="mt-8 space-y-3 border-t border-navy-100 pt-6" data-id="element-912" data-mp="2p5ag0zpcjdsj">
              {whatHappensNext.map(item => <li key={item} className="flex gap-3 font-sans text-[0.875rem] leading-relaxed text-charcoal-500" data-id="element-913" data-mp="333h3rn0kzxeh">
                  <span aria-hidden="true" className="mt-2 h-px w-3 shrink-0 bg-gold" data-id="element-914" data-mp="2y5yu1ueqpe5f" />
                  {item}
                </li>)}
            </ul>
            <div className="mt-9 flex flex-wrap gap-3" data-id="element-915" data-mp="26f2hkuwldbio">
              <ButtonLink to="/opportunities" size="sm" data-id="element-916" data-mp="26c6n5gjyd4yz">
                Browse leadership opportunities
              </ButtonLink>
              <ButtonLink to="/sign-in" size="sm" variant="secondary" data-id="element-917" data-mp="2fefu5wg0ig7m">
                Sign in to your portal
              </ButtonLink>
            </div>
          </div>
        </Section>
      </main>;
  }
  return <main id="main" data-id="element-918" data-mp="1mp8wt0sanwny">
      {/* Page header */}
      <div className="on-dark bg-ink" data-id="element-919" data-mp="1mofxgkvo09fu">
        <Shell className="py-14 md:py-20" data-id="element-920" data-mp="1258i6q72w57l">
          <div className="max-w-2xl animate-rise-in" data-id="element-921" data-mp="1269s2rccylnq">
            <GoldRule data-id="element-922" data-mp="165a9q6j3ni95" />
            <Eyebrow onDark className="mt-5" data-id="element-923" data-mp="166tsq4w547em">
              Join Our Network
            </Eyebrow>
            <h1 className="mt-3 font-serif text-[2rem] leading-[1.12] tracking-[-0.015em] text-parchment md:text-[2.75rem]" data-id="element-924" data-mp="1b4dc1j7p3adf">
              Register your interest
            </h1>
            <p className="mt-5 font-serif text-[1.0625rem] leading-relaxed text-navy-100" data-id="element-925" data-mp="1l447po7zrvf3">
              Tell us about your background and ambitions. Create your candidate portal account below — our team reviews
              every submission and will share confidential opportunities directly to your portal.
            </p>
          </div>
        </Shell>
      </div>

      <Section tone="parchment" data-id="element-926" data-mp="23tsswpm1jxh9">
        <div className="grid gap-12 lg:grid-cols-[1.5fr_1fr] lg:gap-16" data-id="element-927" data-mp="23uvgooo2hjsy">
          {/* Form */}
          <div className="border-t-2 border-ink bg-white p-6 md:p-10" data-id="element-928" data-mp="28tymibbvfu1z">
            <h2 className="font-serif text-[1.375rem] text-ink" data-id="element-929" data-mp="3taob4eb1y15t">Candidate registration</h2>
            <p className="mt-2 font-sans text-[0.8125rem] text-charcoal-300" data-id="element-930" data-mp="3obuwujnxy64l">
              Fields marked with an asterisk are required. Everything else can be completed later in your portal.
            </p>

            {errorList.length ? <div ref={errorSummaryRef} tabIndex={-1} role="alert" className="mt-6 animate-fade-in border-l-2 border-state-negative bg-parchment p-4 outline-none" data-id="element-931" data-mp="3eeyvqz0dgma2">
                <p className="font-sans text-[0.875rem] font-medium text-ink" data-id="element-932" data-mp="azb11jpmq336">
                  {errorList.length === 1 ? 'One field needs your attention before we can continue.' : `${errorList.length} fields need your attention before we can continue.`}
                </p>
                <ul className="mt-2 space-y-1" data-id="element-933" data-mp="tw2pe7k138e3">
                  {errorList.map(([key, message]) => <li key={key} data-id="element-934" data-mp="ib8npklyg7qe">
                      <a href={`#${fieldId(key)}`} className="font-sans text-[0.8125rem] text-state-negative underline underline-offset-2" data-id="element-935" data-mp="icjultap5vlq">
                        {message}
                      </a>
                    </li>)}
                </ul>
              </div> : null}

            <form noValidate onSubmit={handleSubmit} className="mt-8 space-y-6" data-id="element-936" data-mp="119uoeydk7wts">
              <div data-id="element-937" data-mp="x9aqvtp2qr74">
                <label htmlFor={fieldId('fullName')} className={labelClass} data-id="element-938" data-mp="x9u97bimzhlo">
                  Full name <span className="text-gold-700" data-id="element-939" data-mp="1b0z7oaa9q3uf">*</span>
                </label>
                <input id={fieldId('fullName')} name="fullName" type="text" autoComplete="name" required value={form.fullName} onChange={update('fullName')} aria-invalid={Boolean(errors.fullName)} aria-describedby={errors.fullName ? errorId('fullName') : undefined} className={controlClass(Boolean(errors.fullName))} data-id="element-940" data-mp="161mivjzu6mp7" />
                <FieldError id={errorId('fullName')} message={errors.fullName} data-id="element-941" data-mp="1pno2vzxhluv0" />
              </div>

              <div data-id="element-942" data-mp="1ulptr715myh1">
                <label htmlFor={fieldId('email')} className={labelClass} data-id="element-943" data-mp="1uky041r4bate">
                  Email <span className="text-gold-700" data-id="element-944" data-mp="1fx35ugxgoye7">*</span>
                </label>
                <input id={fieldId('email')} name="email" type="email" autoComplete="email" required value={form.email} onChange={update('email')} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? errorId('email') : undefined} className={controlClass(Boolean(errors.email))} data-id="element-945" data-mp="1kntcryljd9b0" />
                <FieldError id={errorId('email')} message={errors.email} data-id="element-946" data-mp="isyrlxe3zo75" />
              </div>

              <div className="grid gap-6 sm:grid-cols-[2fr_1fr]" data-id="element-947" data-mp="e28lghxuzhbe">
                <div data-id="element-948" data-mp="e0xfqckurzhn">
                  <label htmlFor={fieldId('professionalTitle')} className={labelClass} data-id="element-949" data-mp="e0dwmsjgv521">
                    Professional title
                  </label>
                  <input id={fieldId('professionalTitle')} name="professionalTitle" type="text" autoComplete="organization-title" value={form.professionalTitle} onChange={update('professionalTitle')} placeholder="Chief Operating Officer" className={controlClass(false)} data-id="element-950" data-mp="45dmjxxobewl" />
                </div>
                <div data-id="element-951" data-mp="3ckdtl41nvmrt">
                  <label htmlFor={fieldId('yearsExperience')} className={labelClass} data-id="element-952" data-mp="3ckx9kf7r4137">
                    Years of experience
                  </label>
                  <input id={fieldId('yearsExperience')} name="yearsExperience" type="number" inputMode="numeric" min={0} max={60} value={form.yearsExperience} onChange={update('yearsExperience')} aria-invalid={Boolean(errors.yearsExperience)} aria-describedby={errors.yearsExperience ? errorId('yearsExperience') : undefined} className={controlClass(Boolean(errors.yearsExperience))} data-id="element-953" data-mp="37n43rnad193g" />
                  <FieldError id={errorId('yearsExperience')} message={errors.yearsExperience} data-id="element-954" data-mp="2fqwc7ojgyysn" />
                </div>
              </div>

              <div data-id="element-955" data-mp="2kpz5ycu01ilb">
                <label htmlFor={fieldId('linkedInUrl')} className={labelClass} data-id="element-956" data-mp="2koejlahu95yj">
                  LinkedIn profile
                </label>
                <input id={fieldId('linkedInUrl')} name="linkedInUrl" type="url" autoComplete="url" value={form.linkedInUrl} onChange={update('linkedInUrl')} placeholder="linkedin.com/in/yourname" aria-invalid={Boolean(errors.linkedInUrl)} aria-describedby={errors.linkedInUrl ? errorId('linkedInUrl') : undefined} className={controlClass(Boolean(errors.linkedInUrl))} data-id="element-957" data-mp="qby7ht2d2xlr" />
                <FieldError id={errorId('linkedInUrl')} message={errors.linkedInUrl} data-id="element-958" data-mp="1y0pq33c49crq" />
              </div>

              <fieldset className="border-t border-navy-100 pt-6" data-id="element-959" data-mp="26vdvcthoyugj">
                <legend className="flex items-center gap-2 pr-3 font-sans text-micro uppercase tracking-[0.14em] text-charcoal-300" data-id="element-960" data-mp="26wozwviyo68p">
                  <LockIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-961" data-mp="1ef36mw979sc1" />
                  Portal access
                </legend>
                <div className="mt-4 grid gap-6 sm:grid-cols-2" data-id="element-962" data-mp="1aeawm0si7jep">
                  <div data-id="element-963" data-mp="109kqrjtzbv57">
                    <label htmlFor={fieldId('password')} className={labelClass} data-id="element-964" data-mp="1091bkavprkuv">
                      Portal password <span className="text-gold-700" data-id="element-965" data-mp="150sxu97u74f7">*</span>
                    </label>
                    <input id={fieldId('password')} name="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required value={form.password} onChange={update('password')} aria-invalid={Boolean(errors.password)} aria-describedby={errors.password ? errorId('password') : `${uid}-password-hint`} className={controlClass(Boolean(errors.password))} data-id="element-966" data-mp="qbn0yrwh1qah" />
                    {errors.password ? <FieldError id={errorId('password')} message={errors.password} data-id="element-967" data-mp="3tqsk83bzdoov" /> : <p id={`${uid}-password-hint`} className={hintClass} data-id="element-968" data-mp="2jg0phnmkz5s">
                        At least 10 characters. This password is only ever used for your Velnoris portal.
                      </p>}
                  </div>
                  <div data-id="element-969" data-mp="1vhmooogs2zeu">
                    <label htmlFor={fieldId('confirmPassword')} className={labelClass} data-id="element-970" data-mp="1vh3e5stgjdau">
                      Confirm password <span className="text-gold-700" data-id="element-971" data-mp="1qjbgsja1myz9">*</span>
                    </label>
                    <input id={fieldId('confirmPassword')} name="confirmPassword" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required value={form.confirmPassword} onChange={update('confirmPassword')} aria-invalid={Boolean(errors.confirmPassword)} aria-describedby={errors.confirmPassword ? errorId('confirmPassword') : undefined} className={controlClass(Boolean(errors.confirmPassword))} data-id="element-972" data-mp="1lm1s55uharch" />
                    <FieldError id={errorId('confirmPassword')} message={errors.confirmPassword} data-id="element-973" data-mp="3egubexrhw6ax" />
                  </div>
                </div>
                <button type="button" onClick={() => setShowPassword(v => !v)} aria-pressed={showPassword} className="mt-3 inline-flex items-center gap-1.5 font-sans text-[0.75rem] text-navy-600 underline underline-offset-2 transition-colors duration-120 hover:text-ink" data-id="element-974" data-mp="3j8kovclqba6k">
                  {showPassword ? <EyeOffIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-975" data-mp="30g9t02phschy" /> : <EyeIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-976" data-mp="35hz6dp06t297" />}
                  {showPassword ? 'Hide passwords' : 'Show passwords'}
                </button>
              </fieldset>

              <div className="border-t border-navy-100 pt-6" data-id="element-977" data-mp="ksjjh5439xou">
                <label htmlFor={fieldId('motivation')} className={labelClass} data-id="element-978" data-mp="15v98rr91eze9">
                  Why do you want to work with us?
                </label>
                <textarea id={fieldId('motivation')} name="motivation" rows={5} value={form.motivation} onChange={update('motivation')} className={`${controlClass(false)} resize-y`} data-id="element-979" data-mp="1fkanle3ujaqn" />
                <p className={hintClass} data-id="element-980" data-mp="1kjnj0n8s9q4u">
                  Optional. A few sentences on what you are looking for next is more useful to us than a full career
                  history.
                </p>
              </div>

              <div className="border-t border-navy-100 pt-6" data-id="element-981" data-mp="1tl3ja25vwbvc">
                <Button type="submit" size="lg" data-id="element-982" data-mp="45470c6yy6bh">
                  Submit Registration
                </Button>
                <p className="mt-5 max-w-xl font-sans text-[0.75rem] leading-relaxed text-charcoal-500" data-id="element-983" data-mp="3vbotdim2wq26">
                  By submitting, you consent to Velnoris Executive Network storing and reviewing your information for
                  recruitment purposes. Your password creates a candidate portal account used only to view opportunities
                  we share with you.
                </p>
                <p className="mt-3 font-sans text-[0.75rem] text-charcoal-300" data-id="element-984" data-mp="2625y2m7vd0jm">
                  Read our{' '}
                  <Link to="/legal/privacy-policy" className="underline underline-offset-2 hover:text-ink" data-id="element-985" data-mp="1s2n18pkidbmm">
                    privacy policy
                  </Link>{' '}
                  and{' '}
                  <Link to="/legal/candidate-consent" className="underline underline-offset-2 hover:text-ink" data-id="element-986" data-mp="1i5igmu6b9o7x">
                    candidate consent statement
                  </Link>
                  .
                </p>
              </div>
            </form>
          </div>

          {/* Supporting rail */}
          <aside aria-labelledby="register-aside-heading" className="lg:pt-2" data-id="element-987" data-mp="3hgrwhcb9w97o">
            <GoldRule data-id="element-988" data-mp="ow3d9lug4ml0" />
            <h2 id="register-aside-heading" className="mt-5 font-serif text-[1.25rem] text-ink" data-id="element-989" data-mp="ovtizm2dbt9i">
              What happens after you register
            </h2>
            <ul className="mt-6 space-y-5" data-id="element-990" data-mp="fw6tnymwydwt">
              {whatHappensNext.map(item => <li key={item} className="border-l-2 border-navy-100 pl-5 font-sans text-[0.875rem] leading-relaxed text-charcoal-500" data-id="element-991" data-mp="kv1bax959wfc">
                  {item}
                </li>)}
            </ul>

            <div className="mt-10 border-t border-navy-100 pt-6" data-id="element-992" data-mp="1cmpgmzti9ioo">
              <h3 className="font-serif text-[1rem] text-ink" data-id="element-993" data-mp="1cjuzbh5sgdzh">We will never ask you for</h3>
              <ul className="mt-3 space-y-2 font-sans text-[0.8125rem] leading-relaxed text-charcoal-500" data-id="element-994" data-mp="yoirc82mgr0m">
                <li data-id="element-995" data-mp="13nc94coxz46l">A password for any account other than this portal.</li>
                <li data-id="element-996" data-mp="3jbc72tfqri3a">Government identifiers, banking details or payment for consideration.</li>
                <li data-id="element-997" data-mp="3odbbm6n7wl1t">Documents before a consultant has explained why they are needed.</li>
              </ul>
            </div>

            <div className="mt-10 border-t border-navy-100 pt-6" data-id="element-998" data-mp="3gu21ucasukc8">
              <p className="font-sans text-[0.8125rem] text-charcoal-500" data-id="element-999" data-mp="3bsmgsk8mgdtu">
                Already registered?{' '}
                <Link to="/sign-in" className="text-ink underline underline-offset-2" data-id="element-1000" data-mp="3ps47j55ha15u">
                  Sign in to your portal
                </Link>
                .
              </p>
              <p className="mt-2 font-sans text-[0.8125rem] text-charcoal-500" data-id="element-1001" data-mp="2x4rlcw38bvd2">
                Hiring at board or executive level?{' '}
                <Link to="/for-employers" className="text-ink underline underline-offset-2" data-id="element-1002" data-mp="36u1gylxa7w5x">
                  Discuss a search mandate
                </Link>
                .
              </p>
            </div>
          </aside>
        </div>
      </Section>
    </main>;
}